<template>
    <div>
        <div v-if="accessPage === 'dropDown'" class="flex items-center justify-center px-3">
            <BaseButton size="custom" variant="outline" @click="openDrawer"
                class="h-[40px] w-full !rounded-none">
                <i class="ri-book-read-line text-lg text-[#262626] pr-1"></i>
                <span class="text-[#171717] text-base font-normal">
                    {{ $t('orderTemplateInvoice.browseFullCatalogTitle') }}
                </span>
            </BaseButton>
        </div>
        <BaseButton size="custom" variant="outline" v-else-if="accessPage === 'orderTemplate'" @click="openDrawer"
            id="order-template-browse-full-catalog-button"
            class="ml-1 hover:!bg-gray-300 text-base px-2 !rounded-md min-h-[32px]">
            {{ $t('orderTemplateInvoice.browseFullCatalogTitle') }}
            <i class="ml-1 ri-arrow-right-wide-fill text-[20px] text-[#000000]"></i>
        </BaseButton>

        <BaseButton size="custom" variant="outline" v-else-if="accessPage === 'orderBillOfMaterial'" id="order-catalog-browse-catalog-modal"
            @click="openDrawer"
            class="w-full md:w-[270px] items-center px-4 py-2 !rounded-lg text-base focus:ring-2 focus:ring-brand-primary whitespace-nowrap">
            {{ $t('orderTemplateInvoice.browseFullCatalogTitle') }}
            <i class="ml-1 ri-arrow-right-wide-fill text-[20px] text-[#000000]"></i>
        </BaseButton>

        <!-- For Create new estimate page -->
        <BaseButton size="custom" variant="outline" v-else-if="accessPage === 'orderEstimates'" id="order-catalog-browse-catalog-modal"
            @click="openDrawer"
            class="w-full h-12 flex items-center justify-center gap-2 !rounded text-base whitespace-nowrap px-3 md:w-max leading-4">
            {{ $t('orderTemplateInvoice.browseFullCatalogTitle') }}
            <i class="ri-arrow-right-s-line text-xl text-[#464646]"></i>
        </BaseButton>

        <BaseButton size="custom" variant="outline" v-else id="order-catalog-browse-catalog-modal" @click="openDrawer"
            class="w-full md:w-[270px] items-center px-4 py-2 !rounded-lg text-base focus:ring-2 focus:ring-brand-primary whitespace-nowrap">
            {{ $t('orderTemplateInvoice.browseFullCatalogTitle') }}
        </BaseButton>
        <!-- Overlay -->
        <transition name="fade">
            <div v-if="open" class="fixed inset-0 bg-[#0006] z-[9998]" id="order-catalog-close-catalog-modal"
                @click="closeDrawer()"></div>
        </transition>

        <!-- Right Drawer -->
        <transition name="slide">
            <div v-if="open"
                class="fixed top-0 right-0 h-screen w-full md:w-[90%] bg-[#ffff] shadow-2xl z-[9999] flex flex-col">
                <PageLoader v-if="globalCatalogStore.loadingProductInBOM"/>
                <!-- Header -->
                <OrderCatalogSearchBar @close-modal="closeDrawer()" v-if="showSearchBar"/>
                <div class="flex justify-end items-center p-3 border-b border-[#D1D1D1]" v-else>
                    <IconButton
                        id="order-catalog-header-close-modal"
                        icon="ri-close-line"
                        ariaLabel="Close catalog"
                        class="!text-[#464646] hover:!text-[#171717] text-2xl leading-none"
                        @click="closeDrawer()" />
                </div>
                <component 
                    class="px-4 pb-4 overflow-y-auto" 
                    :is="componentToShow" 
                    v-if="isFullPageLayout"
                    @back="goBack" />

                <div v-else id="product-catalog-container" class="flex flex-col lg:flex-row gap-6 p-4 overflow-y-auto">
                    <!-- Left Filters -->
                    <aside class="p-1 rounded-lg bg-[#FFF] flex flex-col">
                        <CatalogFilters />
                    </aside>

                    <!-- Right Content -->
                    <main class="w-full">
                        <component :is="componentToShow" />
                    </main>
                </div>
            </div>
        </transition>
    </div>
</template>
<script setup>
import { ref, computed, watch } from "vue";
import OrderCatalogSearchBar from "./OrderCatalogSearchBar.vue";
import CatalogFilters from '@/components/GlobalCatalog/CatalogFilters.vue'
import Products from '@/components/GlobalCatalog/Products/Products.vue';
import ProductDetail from '@/components/GlobalCatalog/Products/ProductDetail.vue';
import NoSearchResults from "@/components/GlobalCatalog/NoSearchResults.vue";
import CatalogCategoryTemplates from '@/components/GlobalCatalog/CatalogCategoryTemplates.vue';
import PageLoader from '@/components/common/PageLoader.vue'
import BaseButton from '@/components/atoms/buttons/BaseButton.vue';
import IconButton from '@/components/atoms/buttons/IconButton.vue';
import { useGlobalCatalogStore } from "@/stores/globalCatalogStore";
import { useI18n } from 'vue-i18n'
import { getLanguage } from '@/utilities/cookies';

const globalCatalogStore = useGlobalCatalogStore();

const componentMap = {
    'CatalogCategoryTemplates': CatalogCategoryTemplates,
    'Products': Products,
    'ProductDetail': ProductDetail,
    'NoSearchResults': NoSearchResults
};
const isFullPageLayout = computed(() => ['ProductDetail'].includes(globalCatalogStore.componentToShow))
// The component switching is handled in the store according to the selectedCategories
const componentToShow = computed(() => componentMap[globalCatalogStore.componentToShow])

const props = defineProps({
    accessPage: {
        type: String
    },
    categoryName: {
        type: String,
        default: ''
    },
    currentTierId: {
        type: [Number, String],
        default: null
    },
    deliveryType: {
        type: String,
        default: null
    }
})
const { t, locale } = useI18n()
locale.value = getLanguage();

const open = ref(false);

watch(open, (isOpen) => {
    document.body.style.overflow = isOpen ? "hidden" : "";
});
const closeOnBack = ref(false);
const showSearchBar = computed(() => closeOnBack.value === false)

function openDrawer() {
    if (props.categoryName) {
        const category = globalCatalogStore.categories.find((c) => c.CategoryName === props.categoryName);
        if(category) {
            globalCatalogStore.setIsSelectedForCategories(category, true)
        }
    }

    // Set current tier ID for estimate flows (null for non-estimate flows)
    globalCatalogStore.currentTierId = props.currentTierId;
    
    // Set delivery type from parent component (e.g., BOM)
    if (props.deliveryType) {
        globalCatalogStore.bomItemDeliveryType = props.deliveryType;
    }

    open.value = true;
    globalCatalogStore.isOpenCatalogSlider = true;
    globalCatalogStore.hasUserSelectedVariant = true;
    globalCatalogStore.isTemplateSingleton = false;
}

function closeDrawer() {
    open.value = false;
    closeOnBack.value = false;
    globalCatalogStore.isOpenCatalogSlider = false;
    globalCatalogStore.currentTierId = null;
    globalCatalogStore.bomItemDeliveryType = null;
    globalCatalogStore.clearAllFilters();
    globalCatalogStore.productSearchTerm = '';
    localStorage.removeItem('productSearchTerm');
}
const goBack = () => {
    if (closeOnBack.value) {
        closeDrawer();
    }
}
defineExpose({ closeOnBack, open });
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
    transition: transform 0.4s ease;
}

.slide-enter-from,
.slide-leave-to {
    transform: translateX(100%);
}
</style>